/**
 * Badge Component
 *
 * A versatile badge component that wraps the ShadCN badge component.
 * Supports different visual variants including a custom subtle variant.
 */
import * as React from 'react';

import { Badge as ShadcnBadge } from '@ui/components/ui/badge';
import { cn } from '@ui/lib/utils';

export interface BadgeProps {
  /**
   * Badge content
   * This content will be displayed inside the badge
   */
  children: React.ReactNode;

  /**
   * Visual variant of the badge
   * - default: Primary color background with white text
   * - secondary: Secondary color background
   * - destructive: Red background for errors/warnings
   * - outline: Transparent background with border
   * - subtle: Light neutral background with subtle styling
   * @default 'default'
   */
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'subtle';

  /**
   * Optional icon to display before the text
   */
  icon?: React.ReactNode;

  /**
   * Optional additional class names
   */
  className?: string;
}

/**
 * Badge component for displaying status, categories, or labels
 *
 * @example
 * ```tsx
 * <Badge variant="subtle">
 *   Category
 * </Badge>
 * ```
 */
export const Badge = ({ children, variant = 'default', icon, className, ...props }: BadgeProps) => {
  // Handle the custom subtle variant
  if (variant === 'subtle') {
    return (
      <div
        className={cn(
          'inline-flex items-center rounded-sm border px-2.5 py-0.5 text-xs font-semibold font-manrope transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'border-neutral-200 bg-neutral-0 text-neutral-600 shadow-md',
          className
        )}
        {...props}
      >
        {icon && <span className="mr-1.5">{icon}</span>}
        {children}
      </div>
    );
  }

  // For all other variants, use the base ShadcnBadge
  return (
    <ShadcnBadge variant={variant} className={className} icon={icon} {...props}>
      {children}
    </ShadcnBadge>
  );
};
